import React, {useContext, useEffect, useState} from 'react';
import {Button, Grid} from '@material-ui/core';
import {FormProvider} from 'react-hook-form';
import {CircularProgress} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {useGetMMLDActive} from 'hooks';
import {P, H6} from 'components/TypographyShortcuts';
import {UserContext} from 'context/UserContext';
import fields from './fields';
import {useTheme, makeStyles} from '@material-ui/core/styles';
import {PersonSearchLight} from 'components/icons/icons';
import {PersonSearch} from 'components/icons/icons';
import {MMLDFormFields} from 'components/mmld/MMLDDialog';
import {useNonPersistentFormMethods} from 'components/forms/hooks';
import {LinearDeterminate} from 'components/LinearDeterminate';
import {Show} from 'components';

const useStyles = makeStyles(theme => ({
    personSearchIcon: {
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '3em',
        height: '3em',
    },
}));

const InlineAddMMC = () => {
    const theme = useTheme();
    const classes = useStyles();
    const {refetchUser, user} = useContext(UserContext);
    const [success, setSuccess] = useState(false);
    const pushTimeout = 5000;
    const methods = useNonPersistentFormMethods({
        initialValues: {lastName: user?.lastName, refNumber: null},
        schema: {
            lastName: fields.lastName.validation,
            refNumber: fields.refNumber.validation,
        },
    });

    const {get, data: mmld, isLoading: loading, error} = useGetMMLDActive();
    const onSubmit = data => {
        get({
            lastname: data?.lastName?.replaceAll("'", "''"),
            refnum: data?.refNumber,
            only_recent: true,
            fetch: false,
        });
    };

    useEffect(() => {
        if (mmld && mmld?.status !== 'error') {
            setSuccess(true);
            setTimeout(() => {
                refetchUser();
            }, pushTimeout);
        }
    }, [mmld, refetchUser]);

    const noMmldMatch = mmld?.status === 'error';

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Grid container spacing={2} style={{padding: '1em'}}>
                    <Grid item xs={12} key='preamble'>
                        <H6 align='center'>
                            To assess your qualifications and keep track of
                            expiration dates, we need to know what credentials you
                            already have. Use our Easy Lookup tool to add your MMC!
                        </H6>
                    </Grid>
                    <Grid item xs={12} key='icon'>
                        {theme.palette.type === 'dark' ? (
                            <PersonSearchLight
                                className={classes.personSearchIcon}
                            />
                        ) : (
                            <PersonSearch className={classes.personSearchIcon} />
                        )}
                    </Grid>
                    <Grid item xs={12} key='more-preamble'>
                        <H6 align='center'>
                            To use the Easy Lookup tool, enter your last name (as it
                            appears on your MMC) and reference number below.
                        </H6>
                    </Grid>
                    <MMLDFormFields />
                    <Grid item xs={12}>
                        <Button
                            type='submit'
                            variant='contained'
                            color='secondary'
                            startIcon={<PersonSearchLight />}
                        >
                            Easy Lookup
                        </Button>
                    </Grid>
                    <Show condition={loading}>
                        <Grid item xs={12} justifyContent='center'>
                            <Alert severity='info'>
                                <P>MMC lookup takes about 10 seconds. Hold tight!</P>
                                <CircularProgress />
                            </Alert>
                        </Grid>
                    </Show>
                    <Show condition={!loading}>
                        <Grid item xs={12} />
                    </Show>
                    <Show condition={noMmldMatch}>
                        <Grid item xs={12} justifyContent='center'>
                            <Alert severity='warning'>
                                No MMC found for that last name and reference number.
                                Please double check the info and try the search
                                again.
                            </Alert>
                        </Grid>
                    </Show>
                    <Show condition={error}>
                        <Grid item xs={12} justifyContent='center'>
                            <Alert severity='warning'>
                                Something went wrong with our system. Please try
                                again later.
                            </Alert>
                        </Grid>
                    </Show>
                    <Show condition={!error && success}>
                        <Grid item xs={12} justifyContent='center'>
                            <Alert severity='success'>
                                We found your MMC! You will be automatically
                                redirected in {parseInt(pushTimeout / 1000)} seconds
                                <LinearDeterminate duration={pushTimeout} />
                            </Alert>
                        </Grid>
                    </Show>
                    <Show condition={!error && !success}>
                        <Grid item xs={12} />
                    </Show>
                </Grid>
            </form>
        </FormProvider>
    );
};

export default InlineAddMMC;
